<template>
  <a-form
    :layout="searchColumnCount > 0 ? 'horizontal' : 'inline'"
    :class="{ 'search-form-grid-inner': searchColumnCount > 0 }"
  >
    <template
      v-for="condition in dataSource"
      :key="condition.field_name"
    >
      <div
        v-if="condition.is_group"
        class="search-field-group-title"
      >
        {{ condition.label }}
      </div>
      <a-form-item v-else>
        <template #label>
          <span
            :title="condition.label"
            class="search-item-label-text"
          >
            {{ condition.label }}
          </span>
        </template>
        <a-input-group
          compact
          size="small"
        >
          <a-select
            v-if="condition.operator_list.length > 0 && searchType === 'advanced'"
            style="width: 100px"
            :dropdownMatchSelectWidth="false"
            :value="condition.operator"
            @change="onChange(condition.field_name, 'operator', $event)"
            size="small"
          >
            <a-select-option
              v-for="item in condition.operator_list"
              :value="item"
              :key="item"
            >
              <a-tooltip :title="operator_type_map[item]">
                {{ operator_type_map[item] }}
              </a-tooltip>
            </a-select-option>
          </a-select>
          <template
            v-if="['is_or_not_blank_or_null', 'is_or_not_null'].includes(condition.operator)"
          >
            <SmallTableSerarchField
              :is_show_label="false"
              :label="condition.field_name"
              :value="getValue(condition)"
              :field="{
                ...condition,
                type: 'select',
                mode: 'default',
                format: 'choices',
                class_name: 'str',
                placeholder: $t('请选择是否为空'),
              }"
              :formModel="formModel"
              :options="is_or_not_blank_or_null_options"
              :validateInfos="validateInfos"
              @update:value="onChange(condition.field_name, 'value', $event)"
              :field_width="condition.field_width"
            />
          </template>
          <template
            v-else-if="condition.operator === 'between' || condition.operator === 'notbetween'"
          >
            <template v-if="condition.type === 'datepicker'">
              <SmallTableSerarchField
                :is_show_label="false"
                :label="condition.field_name"
                :value="getValue(condition)"
                :field="{ ...condition, type: 'rangepicker', format: condition.dateFormat }"
                :formModel="formModel"
                :validateInfos="validateInfos"
                :field_width="condition.field_width * 2"
                @update:value="onChange(condition.field_name, 'value', $event)"
              />
            </template>
            <template v-else-if="condition.type === 'monthpicker'">
              <SmallTableSerarchField
                :is_show_label="false"
                :label="condition.field_name"
                :value="getValue(condition)"
                :field="{
                  ...condition,
                  type: 'monthrangepicker',
                }"
                :formModel="formModel"
                :validateInfos="validateInfos"
                :field_width="condition.field_width * 2"
                @update:value="onChange(condition.field_name, 'value', $event)"
              />
            </template>
            <template v-else>
              <SmallTableSerarchField
                :is_show_label="false"
                :label="condition.field_name"
                :value="condition.value?.[0]"
                :field="condition"
                :formModel="formModel"
                :options="condition.options"
                :validateInfos="validateInfos"
                :field_width="condition.field_width"
                @update:value="onChange(condition.field_name, 'value', $event, 0)"
              />
              <span class="range">~</span>
              <SmallTableSerarchField
                :is_show_label="false"
                :label="condition.field_name"
                :value="condition.value?.[1]"
                :field="condition"
                :formModel="formModel"
                :options="condition.options"
                :validateInfos="validateInfos"
                :field_width="condition.field_width"
                @update:value="onChange(condition.field_name, 'value', $event, 1)"
              />
            </template>
          </template>
          <template v-else-if="condition.operator === '_eq'">
            <SmallTableSerarchField
              :is_show_label="false"
              :label="condition.field_name"
              :value="getValue(condition)"
              :field="{
                ...condition,
                type: 'select',
                mode: 'default',
                format: 'choices',
                class_name: 'str',
                placeholder: '',
              }"
              :formModel="formModel"
              :options="options_for_date_eq"
              :validateInfos="validateInfos"
              :field_width="condition.field_width"
              @update:value="onChange(condition.field_name, 'value', $event)"
            />
          </template>
          <template v-else-if="condition.type === 'datepicker' || condition.type === 'monthpicker'">
            <SmallTableSerarchField
              :is_show_label="false"
              :label="condition.field_name"
              :value="getValue(condition)"
              :field="{ ...condition, format: condition.dateFormat }"
              :formModel="formModel"
              :options="condition.options"
              :validateInfos="validateInfos"
              :field_width="condition.field_width"
              @update:value="onChange(condition.field_name, 'value', $event)"
            />
          </template>
          <template v-else>
            <SmallTableSerarchField
              :is_show_label="false"
              :label="condition.field_name"
              :value="getValue(condition)"
              :field="{ ...condition, name: condition?.field_name }"
              :formModel="formModel"
              :options="condition.options"
              :validateInfos="validateInfos"
              :field_width="condition.field_width"
              @update:value="onChange(condition.field_name, 'value', $event)"
            />
          </template>
        </a-input-group>
      </a-form-item>
    </template>
    <div
      class="btn-content"
      v-if="showQueryBtn && !autoRefreshOnSearchChange"
    >
      <a-button
        size="small"
        type="primary"
        @click="handleRefresh"
      >
        查询
      </a-button>
    </div>
  </a-form>
</template>

<script setup lang="ts">
import { cloneDeep, debounce } from 'lodash';
import { computed, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { SearchConditionItem, SearchConfig, SearchType, ViewSearchItem } from './typing';
import SmallTableSerarchField from 'liyu-pc-base/components/FormRender/SmallTableSerarchField.vue';
import { getDefaultSearchOperator, getParamsCount, operator_type_map, options_for_date_eq } from './utils';
import { isEmpty, isNotEmpty } from 'liyu-pc-base/utils/function';

defineOptions({
  name: 'SearchForm',
});

const props = defineProps({
  searchConfig: {
    type: Array as PropType<SearchConfig[]>,
    required: true,
  },
  searchConditions: {
    type: Array as PropType<SearchConditionItem[]>,
    required: true,
  },
  viewSearchConfig: {
    type: Object as PropType<Record<string, ViewSearchItem>>,
    default: () => ({}),
  },
  searchType: {
    type: String as () => SearchType,
    default: 'advanced',
  },
  searchColumnCount: {
    type: Number,
    default: 0,
  },
  autoRefreshOnSearchChange: {
    type: Boolean,
    default: false,
  },
  eventBus: {
    type: Object as any,
    required: false,
  },
  showQueryBtn: {
    type: Boolean,
    default: false,
  },
  disabledShowSearchGroupName: {
    type: Boolean,
    default: false, // 是否显示搜索分组名称，默认不显示
  },
});

const emit = defineEmits(['update:searchConditions', 'change']);

const { t } = useI18n();
const formModel = ref<Record<string, any>>({});
const validateInfos = ref({});

const is_or_not_blank_or_null_options = ref([
  { value: 'yes', label: t('is_or_not_blank_or_null.yes') },
  { value: 'no', label: t('is_or_not_blank_or_null.no') },
]);

const dataSource = computed(() => {
  const searchConditionsMap: Record<string, any> = {};
  for (const condition of props.searchConditions) {
    searchConditionsMap[condition.field_name] = {
      value: condition.value,
      operator: condition.operator,
    };
  }

  const flatData = props.searchConfig
    ?.filter(item => {
      if (props.viewSearchConfig[item.field_name]) {
        return props.viewSearchConfig[item.field_name].checked !== false;
      } else {
        return item.checked !== false;
      }
    })
    ?.map(configItem => {
      const data = searchConditionsMap[configItem.field_name] || {
        operator: getDefaultSearchOperator(configItem),
        value: undefined,
      };
      let formValue = data.value;
      if (isNotEmpty(data.value)) {
        const paramsCount = getParamsCount(data.operator, configItem);
        if (paramsCount === 1 && formValue instanceof Array) {
          formValue = formValue[0];
        }
      }
      formModel.value[configItem.field_name] = formValue;
      return {
        ...configItem,
        value: data.value,
        operator: data.operator,
        model: 'default',
        _order: props.viewSearchConfig[configItem.field_name]?.order || 0,
        field_width: configItem.field_width || 165,
      };
    })
    .sort((a, b) => a._order - b._order);

  const groups: Record<string, any[]> = {};
  const orderList: any[] = [];

  flatData.forEach(item => {
    if (item.group_name) {
      if (!groups[item.group_name]) {
        groups[item.group_name] = [];
        orderList.push({
          field_name: `group_${item.group_name}`,
          label: item.group_name,
          is_group: true,
          _group_key: item.group_name,
        });
      }
      groups[item.group_name].push(item);
    } else {
      orderList.push(item);
    }
  });

  const finalResult: any[] = [];
  orderList.forEach(item => {
    if (item.is_group) {
      if (!props.disabledShowSearchGroupName) {
        finalResult.push(item);
      }
      finalResult.push(...groups[item._group_key]);
    } else {
      finalResult.push(item);
    }
  });

  return finalResult;
});

const getValue = (configItemAndValue: any) => {
  const value_count = getParamsCount(configItemAndValue.operator, configItemAndValue);
  let value = configItemAndValue.value;
  if (value_count == 1) {
    if (value instanceof Array) {
      return value[0] || undefined;
    } else {
      return value || undefined;
    }
  } else if (value_count == 2) {
    if (value instanceof Array) {
      const [first, seconds] = value;
      return [first, seconds];
    } else if (value) {
      const first = value;
      return [first, null];
    } else {
      return [null, null];
    }
  } else {
    return value;
  }
};

const delayRefresh = debounce(() => {
  props.eventBus.emit('refresh');
}, 500);

const onChange = (
  field_name: string,
  type: 'value' | 'operator',
  newValue: any,
  array_index: number | undefined = undefined,
) => {
  const conditions = cloneDeep(props.searchConditions);
  const configItem = props.searchConfig.find(item => item.field_name === field_name);
  let value: any;
  let operator: string;
  let condition: any = conditions.find(item => item.field_name === field_name);
  if (type === 'value') {
    if (condition) {
      operator = condition.operator;
    } else {
      operator = getDefaultSearchOperator(configItem);
    }
    if (array_index !== undefined) {
      const value_count = getParamsCount(newValue, configItem);
      if (condition) {
        value = condition.value;
        if (isEmpty(value) || !(value instanceof Array)) {
          value = [];
          for (let i = 0; i < value_count; i++) {
            value.push(null);
          }
        }
        value[array_index] = newValue;
      } else {
        value = [];
        for (let i = 0; i < value_count; i++) {
          value.push(null);
        }
        value[array_index] = newValue;
      }
    } else {
      if (isNotEmpty(newValue) && !(newValue instanceof Array)) {
        newValue = [newValue];
      }
      value = newValue;
    }
  } else if (type === 'operator') {
    operator = newValue;
    const value_count = getParamsCount(newValue, configItem);
    if (value_count) {
      value = [];
      for (let i = 0; i < value_count; i++) {
        value.push(null);
      }
    } else {
      value = undefined;
    }
  }
  if (condition) {
    condition.operator = operator!;
    condition.value = value;
  } else {
    let format = 'input';
    let class_name = 'str';
    if (configItem) {
      format = configItem.format || 'input';
      class_name = configItem.class_name || 'str';
    }
    condition = {
      field_name: field_name,
      value,
      operator: operator!,
      format,
      class_name,
    };
    conditions.push(condition);
  }
  emit('update:searchConditions', conditions);
  emit('change', conditions);
  if (props.autoRefreshOnSearchChange) {
    delayRefresh();
  }
};

const handleRefresh = () => {
  props.eventBus.emit('refresh');
};
</script>

<style lang="less" scoped>
.search-field-group-title {
  font-weight: bold;
  padding: 8px 4px 4px 4px;
  margin-bottom: 4px;
  border-bottom: 1px solid #f0f0f0;
  color: var(--primary-color, #1890ff);
  display: flex;
  align-items: center;
  width: 100%;
  flex-basis: 100%;
  &:not(:first-child) {
    margin-top: 8px;
  }
}
.search-form-grid-inner {
  display: grid;
  grid-template-columns: repeat(var(--column-count), max-content 1fr);
  gap: 2px 8px;
  align-items: center;
  .search-field-group-title {
    grid-column: span calc(var(--column-count) * 2);
    &:not(:first-child) {
      margin-top: 8px;
    }
  }
  :deep(.ant-form-item) {
    display: contents;
    margin-right: 0;
    margin-bottom: 2px;
    .ant-form-item-row,
    .ant-form-item-label,
    .ant-form-item-control,
    .ant-form-item-control-input,
    .ant-form-item-control-input-content {
      display: contents;
    }
  }
  :deep(.ant-form-item-label) {
    justify-self: end;
    display: flex !important;
    align-items: center;
    max-width: 150px;
    .search-item-label-text {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      padding-right: 8px;
      text-align: right;
    }
  }
  :deep(.ant-input-group) {
    display: flex !important;
    width: 100%;
  }
  .btn-content {
    grid-column: span calc(var(--column-count) * 2);
  }
}
.btn-content {
  padding: 4px;
  margin-left: auto;
}
.range {
  padding: 0 4px;
}
:deep(.ant-select-selection-placeholder) {
  overflow: hidden;
  width: 100%;
}
</style>
